/// <amd-module name="Wiki/PageRenamePanel/PageRenamePanelHelper" />

import { ITfsPageService } from "Tfs/Platform/Page";
import { startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { defaultComparer } from "VSS/Platform/Util/Array";

import { ArtifactUriQueryResult } from "Wiki/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wiki/Clients/Wit/RestClient/WorkItemTracking";
import { ArtifactUriToWorkItemReferencesMap } from "Wiki/PageRenamePanel/IPageRenamePanelSharedInterfaces";
import { UrlEscapeConstants } from "Wiki/ViewCommon/Constants";
import { PathConstants, RepoConstants, SpecialChars, TemplateConstants } from "Wiki/ViewCommon/Constants";
import { encodeHyphenAndSpacesInPath, getLinkFromPath } from "Wiki/ViewCommon/Utils/PathHelper";

const DEFAULT_MAX_WORKITEMS_QUERY_COUNT = 200;

/**
 * Returns wiki page path that would show in url after encoding
 * Example: input: "abc def-efg" output: "abc%20def%252Defg"
 * @param path - PagePath
 */
export function getEncodedWikiPageUrlPath(path: string): string {
    return encodeURIComponent(path.replace(UrlEscapeConstants.HyphenRegExp, UrlEscapeConstants.HyphenEncoding));
}

export function getWikiPagePathFromGitPath(gitPath: string): string | undefined {
    if (!gitPath) {
        return undefined;
    }

    gitPath = gitPath.replace(/-/g, SpecialChars.Space);

    for (let i = 0; i < PathConstants.GitIllegalSpecialCharEscapesRegExp.length; i++) {
        gitPath = gitPath.replace(PathConstants.GitIllegalSpecialCharEscapesRegExp[i], PathConstants.GitIllegalSpecialChars[i]);
    }
    return gitPath;
}

export function getPagePathWithoutRootCharacter(pagePath: string): string {
    if (pagePath && pagePath.length > 0 && startsWith(pagePath, RepoConstants.RootPath)) {
        return pagePath.substring(RepoConstants.RootPath.length);
    }

    return pagePath;
}

export function getHostName(pageContext: IVssPageContext): string | undefined {
    const pageService = pageContext.getService<IVssPageService>("IVssPageService");
    const data = pageService.getData();

    return data ? data.hostName : undefined;
}

export function getProjectName(pageContext: IVssPageContext): string | undefined {
    const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
    const data = tfsPageService.getData();

    return data && data.project ? data.project.name : undefined;
}

export function replaceForwardSlashesToBackSlashes(str: string): string {
    return str.replace(/\//g, "\\");
}

export function escapeRegExp(str: string): string {
    return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
}

export function contains<T>(array: T[], value: T, comparer?: { (a: any, b: any): number }): boolean {
    if (typeof value === "undefined") {
        return false;
    }

    comparer = comparer || defaultComparer;

    for (let i = 0, l = array.length; i < l; i++) {
        if (typeof array[i] !== "undefined" && comparer(array[i], value) === 0) {
            return true;
        }
    }

    return false;
}

/**
 * Making it public for UTs
 * @param initialContent: page content whose links needs to be fixed.
 * @param topLevelPageOldPath: old page path for the top level page that was renamed.
 * @param topLevelPageNewPath: new page path for the top level page that was renamed.
 */
export function getUpdatedContent(
    pageContext: IVssPageContext,
    initialContent: string,
    topLevelPageOldPath: string,
    topLevelPageNewPath: string
): string {
    let pageContent = initialContent;

    const wikiLinkPath = getLinkFromPath(topLevelPageOldPath);
    const escapedOldPathWithForwardSlashes = escapeRegExp(wikiLinkPath);
    const newAbsolutePathWithForwardSlashes = getLinkFromPath(topLevelPageNewPath);
    /**
     * The following regex matches two patterns.
     *  1. [any display name](full page path or url path)
     *  2. [any display name]: full page path or url path
     */
    const wikiPathFromRootRegex = new RegExp(
        "(\\[.+?\\]\\()(\\\\?" +
            escapedOldPathWithForwardSlashes +
            ")((\\/.*?)?(#.+)?\\))" +
            "|" +
            "(\\[.+?\\]:\\s*)(\\\\?" +
            escapedOldPathWithForwardSlashes +
            ")((\\/.*?)?(#.+)?(\\s|$))",
        "gi"
    );
    pageContent = pageContent.replace(wikiPathFromRootRegex, "$1$6" + newAbsolutePathWithForwardSlashes + "$3$8");

    pageContent = updateBrokenTemplateReferences(pageContent, topLevelPageOldPath, topLevelPageNewPath);

    /**
     * A backslash link should be replaced by new backslash link.
     * This is because we only match and replace page path only till root level.
     * If we replace old path that has backslash with new path having forward slashes we may end up with following type of links
     * [link display value](/rootPagePathPostReplacement\childLinkWithBackSlashes\sub-child)
     * Once we end up with mix slashes scenario, we won't be able to search and replace them later.
     */
    const escapedOldPathWithBackSlashes = escapeRegExp(replaceForwardSlashesToBackSlashes(wikiLinkPath));
    const newAbsolutePathLinkWithBackSlashes = replaceForwardSlashesToBackSlashes(getLinkFromPath(topLevelPageNewPath));

    const wikiPathBackSlashRegex = new RegExp(
        "(\\[.+?\\]\\()(\\\\?" +
            escapedOldPathWithBackSlashes +
            ")((\\\\.*?)?(#.+)?\\))" +
            "|" +
            "(\\[.+?\\]:\\s*)(\\\\?" +
            escapedOldPathWithBackSlashes +
            ")((\\/.*?)?(#.+)?(\\s|$))",
        "gi"
    );
    pageContent = pageContent.replace(wikiPathBackSlashRegex, "$1$6" + newAbsolutePathLinkWithBackSlashes + "$3$8");

    const escapedOldUrlPath = escapeRegExp(getEncodedWikiPageUrlPath(topLevelPageOldPath));
    const newUrlPath = getEncodedWikiPageUrlPath(topLevelPageNewPath);
    const urlFormatRegexString = getUrlFormatRegexString(pageContext);

    const urlPathRegex = new RegExp(
        "(\\[.+?\\]\\(" +
            urlFormatRegexString +
            ")(" +
            escapedOldUrlPath +
            ")((%2F.*?)?(&.+)?(#.+)?\\))" +
            "|" +
            "(\\[.+?\\]:\\s*" +
            urlFormatRegexString +
            ")(" +
            escapedOldUrlPath +
            ")((%2F.*?)?(&.+)?(#.+)?(\\s|$))",
        "gi"
    );
    pageContent = pageContent.replace(urlPathRegex, "$1$7" + newUrlPath + "$3$9");

    return pageContent;
}

export function updateBrokenTemplateReferences(pageContent: string, topLevelPageOldPath: string, topLevelPageNewPath: string): string {
    const wikiLinkPath = encodeHyphenAndSpacesInPath(topLevelPageOldPath);
    const escapedOldPathWithForwardSlashes = escapeRegExp(wikiLinkPath);
    const newAbsolutePathWithForwardSlashes = encodeHyphenAndSpacesInPath(topLevelPageNewPath);

    const templatePathRegex = new RegExp(`(${TemplateConstants.Identifier}\\s+)${escapedOldPathWithForwardSlashes}`, "gi");
    pageContent = pageContent.replace(templatePathRegex, `$1${newAbsolutePathWithForwardSlashes}`);
    return pageContent;
}

export function getNewPagePath(pagePathToUpdate: string, topLevelPageOldPath: string, topLevelPageNewPath: string): string {
    if (pagePathToUpdate === topLevelPageOldPath) {
        return topLevelPageNewPath;
    } else {
        // Page is renamed as it is a sub page of top level page which is renamed.
        return pagePathToUpdate.replace(topLevelPageOldPath + "/", topLevelPageNewPath + "/");
    }
}

export function getUrlFormatRegexString(pageContext: IVssPageContext): string {
    /**
     * The Regex string is of following format
     * There can be 3 types of url in use today, they are.
     * New hosted url format: devops.azure.com/<org name>/<project name>/_wiki/wikis
     * Old hosted url format: <org name>.visualStudio.com/<project name>/_wiki/wikis
     * onPremServerUrl:8080/tfs/<collection name>/<project name>/_wiki/wikis
     *
     * In regex, we are matching, <org/collection name> <project name> <_wiki/wikis> <pagePath=>
     * Between every set there can be any number of non new line characters.
     * If it is hosted it org name will be used, else collection name will be used.
     */
    const urlFormatRegexString =
        ".+?" +
        escapeRegExp(getHostName(pageContext) || "") +
        "*?/" +
        escapeRegExp(encodeURI(getProjectName(pageContext) || "")) +
        "/_wiki/wikis" +
        ".+?pagePath=";

    return urlFormatRegexString;
}

export function fetchAllWorkItemReferencesFromArtifactUris(
    pageContext: IVssPageContext,
    wikiPageArtifactUris: string[]
): Promise<ArtifactUriToWorkItemReferencesMap> {
    if (!wikiPageArtifactUris || wikiPageArtifactUris.length === 0) {
        return Promise.resolve({});
    }

    let promise = Promise.resolve({});
    for (let count = 0; count < wikiPageArtifactUris.length; count += DEFAULT_MAX_WORKITEMS_QUERY_COUNT) {
        promise = promise.then((artifactUrisResult: ArtifactUriToWorkItemReferencesMap) => {
            return getWorkItemTrackingClient(pageContext)
                .queryWorkItemsForArtifactUris(
                    {
                        artifactUris: wikiPageArtifactUris.slice(count, count + DEFAULT_MAX_WORKITEMS_QUERY_COUNT)
                    },
                    undefined
                )
                .then((result: ArtifactUriQueryResult) => {
                    Object.assign(artifactUrisResult, result.artifactUrisQueryResult);
                    return artifactUrisResult;
                });
        });
    }

    return promise;
}
